import { ApolloClient, useApolloClient, useMutation } from "@apollo/client";
import { Button, Chip, Stack, Typography } from "@mui/material";
import { GridSortModel } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { STAFF_NOTIFICATION_PREFERENCES_URL } from "components/Notifications/notificationNavigation";
import {
  UnreachedOrganizationReason,
  UnreachedOrganizationsQuery,
  UnreachedOrganizationsQueryVariables,
  UnreachedPersonState,
} from "generated-graphql/graphql";
import { OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useCallback, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { ROLE_LABELS } from "routes/Customer/Settings/Notifications/accountRoleLabels";
import {
  SET_TENANT_NOTIFICATION_SUBSCRIPTION,
  writeSubscribedOrganizations,
} from "routes/Customer/Settings/Notifications/notificationPreferenceDocuments";

import {
  UNREACHED_ORGANIZATION_COUNT,
  UNREACHED_ORGANIZATIONS,
} from "./unreachedOrganizationsQuery";

type UnreachedOrganization = UnreachedOrganizationsQuery["unreachedOrganizations"]["items"][number];
type UnreachedPerson = UnreachedOrganization["people"][number];

const REASON_LABELS: Record<UnreachedOrganizationReason, string> = {
  [UnreachedOrganizationReason.NoSubscribers]: "No one is subscribed",
  [UnreachedOrganizationReason.SubscribersMuted]: "Subscribers turned alerts off",
};

const REASON_COLORS: Record<UnreachedOrganizationReason, "error" | "warning"> = {
  [UnreachedOrganizationReason.NoSubscribers]: "error",
  [UnreachedOrganizationReason.SubscribersMuted]: "warning",
};

const PERSON_STATE_LABELS: Record<UnreachedPersonState, string> = {
  [UnreachedPersonState.NoStaffAccount]: "no staff account",
  [UnreachedPersonState.AccountInactive]: "account inactive",
  [UnreachedPersonState.Unsubscribed]: "unsubscribed",
  [UnreachedPersonState.Muted]: "turned alerts off",
};

const REASONS = Object.values(UnreachedOrganizationReason);

function reasonLabel(value: string): string {
  const reason = REASONS.find((candidate) => candidate === value);
  return reason ? REASON_LABELS[reason] : value;
}

const PERSON_STATES = Object.values(UnreachedPersonState);

/** "Unsubscribed": a person's state as the filter offers it. */
function personStateFilterLabel(value: string): string {
  const state = PERSON_STATES.find((candidate) => candidate === value);
  const label = state ? PERSON_STATE_LABELS[state] : value;
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** "Casey Staff (CSM): unsubscribed" */
function describePerson(person: UnreachedPerson): string {
  const roles = person.roles.map((role) => ROLE_LABELS[role]).join(", ");
  return `${person.name}${roles ? ` (${roles})` : ""}: ${PERSON_STATE_LABELS[person.state]}`;
}

/** Whether the viewer is already subscribed to the organization, and turned its alerts off. */
function isMutedSubscriber(
  organization: UnreachedOrganization,
  userId: string | undefined
): boolean {
  return organization.people.some(
    (person) => person.userId === userId && person.state === UnreachedPersonState.Muted
  );
}

/** The viewer's own alert settings, open on this organization. */
function alertSettingsUrl(tenantId: string): string {
  return `${STAFF_NOTIFICATION_PREFERENCES_URL}?${new URLSearchParams({ org: tenantId })}`;
}

function cachedUnreachedCount(client: ApolloClient<object>): number | undefined {
  return client.readQuery({ query: UNREACHED_ORGANIZATION_COUNT })?.unreachedOrganizations.count;
}

/**
 * Subscribes the viewer to an organization; the list and count are refetched so it leaves.
 * It stays when the viewer's own alerts there are off, and the message says so.
 */
function useSubscribe() {
  const alerts = useAlerts();
  const client = useApolloClient();
  const [subscribingTenantId, setSubscribingTenantId] = useState<string | null>(null);
  const [setSubscription] = useMutation(SET_TENANT_NOTIFICATION_SUBSCRIPTION, {
    update: writeSubscribedOrganizations,
    refetchQueries: [UNREACHED_ORGANIZATIONS, UNREACHED_ORGANIZATION_COUNT],
    awaitRefetchQueries: true,
  });

  const subscribe = useCallback(
    async (organization: UnreachedOrganization) => {
      setSubscribingTenantId(organization.tenantId);
      const countBefore = cachedUnreachedCount(client);
      try {
        await setSubscription({
          variables: { tenantId: organization.tenantId, subscribed: true },
        });
        if (countBefore !== undefined && cachedUnreachedCount(client) === countBefore) {
          alerts.warning(
            `You're now subscribed to ${organization.name}, but your alerts there are turned off.`
          );
        } else {
          alerts.success(`You're now subscribed to ${organization.name}.`);
        }
      } catch (error) {
        alerts.error(
          error instanceof Error ? error.message : "Couldn't subscribe to the organization."
        );
      } finally {
        setSubscribingTenantId(null);
      }
    },
    [alerts, client, setSubscription]
  );

  return { subscribe, subscribingTenantId };
}

function useColumns(): OmnisearchGridColDef<UnreachedOrganization>[] {
  const { user } = useCurrentUser();
  const { subscribe, subscribingTenantId } = useSubscribe();

  return useMemo(
    () => [
      { field: "name", headerName: "Organization", flex: 0.6, minWidth: 160 },
      {
        field: "reason",
        headerName: "Why",
        width: 220,
        filterKeyType: "enum",
        enumValues: REASONS,
        enumPresentationFunction: reasonLabel,
        valueGetter: (_, row) => REASON_LABELS[row.reason],
        renderCell: ({ row }) => (
          <Chip
            size="small"
            variant="outlined"
            color={REASON_COLORS[row.reason]}
            label={REASON_LABELS[row.reason]}
          />
        ),
      },
      {
        field: "people",
        headerName: "Account team and subscribers",
        flex: 1,
        minWidth: 240,
        sortable: false,
        // Finds organizations where anyone listed is in a state, such as an account
        // team member who unsubscribed.
        filterKeyType: "enum",
        enumValues: PERSON_STATES,
        enumPresentationFunction: personStateFilterLabel,
        valueGetter: (_, row) => row.people.map(describePerson).join("; "),
        renderCell: ({ row }) =>
          row.people.length > 0 ? (
            <Stack sx={{ py: 1 }}>
              {row.people.map((person, index) => (
                <Typography key={`${person.userId ?? person.name}-${index}`} variant="body2">
                  {describePerson(person)}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No one on the account team
            </Typography>
          ),
      },
      {
        field: "subscribe",
        headerName: "",
        width: 140,
        sortable: false,
        renderCell: ({ row }) =>
          isMutedSubscriber(row, user?.id) ? (
            <Button
              size="small"
              variant="outlined"
              component={RouterLink}
              to={alertSettingsUrl(row.tenantId)}
            >
              Turn alerts on
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              disabled={subscribingTenantId !== null}
              onClick={() => subscribe(row)}
            >
              {subscribingTenantId === row.tenantId ? "Subscribing…" : "Subscribe"}
            </Button>
          ),
      },
    ],
    [subscribe, subscribingTenantId, user?.id]
  );
}

const INITIAL_SORT_MODEL: GridSortModel = [{ field: "name", sort: "asc" }];
/** A button: nothing a filter could match. */
const EXCLUDE_FILTER_COLUMNS = ["subscribe"];

function getItems(data: UnreachedOrganizationsQuery): UnreachedOrganization[] {
  return data.unreachedOrganizations.items;
}

function getCount(data: UnreachedOrganizationsQuery): number {
  return data.unreachedOrganizations.count;
}

function getRowId(row: UnreachedOrganization): string {
  return row.tenantId;
}

/** Tall enough for every person in the row. */
function getRowHeight(): "auto" {
  return "auto";
}

function buildVariables(
  search: string,
  pagination: { page: number; pageSize: number },
  sort: GridSortModel
): UnreachedOrganizationsQueryVariables {
  return {
    search,
    page: pagination.page,
    pageSize: pagination.pageSize,
    sort: sort.map((s) => ({ field: s.field, sort: s.sort })),
  };
}

/**
 * The "Reaching no one" tab of the staff Notifications screen: every live organization
 * whose Encamp-only alerts reach no staff member, and why.
 */
export function UnreachedOrganizations() {
  useBreadcrumb({ label: "Reaching no one" });
  const columns = useColumns();

  return (
    <OmnisearchDataGridV2
      query={UNREACHED_ORGANIZATIONS}
      getItems={getItems}
      getCount={getCount}
      getRowId={getRowId}
      getRowHeight={getRowHeight}
      buildVariables={buildVariables}
      columns={columns}
      excludeFilterColumns={EXCLUDE_FILTER_COLUMNS}
      initialSortModel={INITIAL_SORT_MODEL}
      persistenceKey="staffUnreachedOrganizations"
    />
  );
}
